import { createPortal } from 'react-dom';
import { useEffect, useId } from 'react';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scroll';
import { OptOutForm } from './OptOutForm';
import { OptOutResult } from './OptOutResult';

interface DoNotSellModalProps {
  onClose: () => void;
  onOptOut: () => void;
  hasResult: boolean;
  isSuccess: boolean;
  isPending: boolean;
}

/**
 * ACDEV-29992 — in-page opt-out modal opened from the Checkout footer link.
 * "Opt out" calls the opt-out API (owned by useGpcMarketingEmailOptOut so the
 * request + result survive Footer remounts) and swaps the content for a
 * success or error result; X / overlay just return to Checkout.
 */
export const DoNotSellModal = ({ onClose, onOptOut, hasResult, isSuccess, isPending }: DoNotSellModalProps) => {
  const scrollLockId = useId();

  useEffect(() => {
    lockBodyScroll(scrollLockId);
    return () => unlockBodyScroll(scrollLockId);
  }, [scrollLockId]);

  // Portaled to document.body so `fixed inset-0` anchors to the viewport. The
  // checkout Modal's transformed ancestor would otherwise trap the overlay.
  return createPortal(
    <div
      data-testid="do-not-sell-modal"
      className="fixed inset-0 z-150 flex items-center justify-center bg-base-black/60 backdrop-blur-sm p-4 "
      role="dialog"
      aria-modal="true"
      aria-labelledby="do-not-sell-modal-title"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-[370px] flex-col gap-7 overflow-y-auto rounded-lg border border-base-border bg-white p-6 shadow-lg max-h-[stretch] landscape:max-w-[450px]"
        onClick={(e) => e.stopPropagation()}
      >
        {hasResult ? (
          <OptOutResult isSuccess={isSuccess} isPending={isPending} onClose={onClose} onRetry={onOptOut} />
        ) : (
          <OptOutForm isPending={isPending} onOptOut={onOptOut} onClose={onClose} />
        )}
      </div>
    </div>,
    document.body
  );
};
